import type { TimeWindow as CommonTimeWindow } from "@hai/common";
import type { SlotWindow, IsoDateWindow } from "./types";

const SLOT_MS = 15 * 60 * 1000; // 900,000ms (15 minutes)

/**
 * Convert slot index to UTC timestamp in milliseconds
 */
export function slotToMs(slot: number): number {
  return slot * SLOT_MS;
}

/**
 * Convert UTC timestamp to slot index
 */
export function msToSlot(ms: number): number {
  return Math.floor(ms / SLOT_MS);
}

/**
 * Convert CommonTimeWindow (with ISO strings) to Date objects
 */
export function timeWindowToDates(window: CommonTimeWindow | SlotWindow): [Date, Date] {
  const asAny = window as Record<string, unknown>;
  const startIso = asAny.startIso as string | undefined;
  const endIso = asAny.endIso as string | undefined;
  const startSlot = asAny.start as number | undefined;
  const endSlot = asAny.end as number | undefined;

  if (typeof startIso === "string" && typeof endIso === "string") {
    return [new Date(startIso), new Date(endIso)];
  }

  if (typeof startSlot === "number" && typeof endSlot === "number") {
    return [new Date(slotToMs(startSlot)), new Date(slotToMs(endSlot))];
  }

  throw new Error("Invalid TimeWindow shape: expected startIso/endIso or start/end slots");
}

/**
 * Format CommonTimeWindow as human-readable string
 */
export function formatTimeWindow(window: CommonTimeWindow, timezone?: string): string {
  const [start, end] = timeWindowToDates(window);
  const dateStr = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
  const startStr = start.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
  const endStr = end.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
  return `${dateStr} • ${startStr}–${endStr}`;
}

function setEndOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setUTCHours(23, 59, 59, 999);
  return copy;
}

export function buildDefaultWindows(now: Date, daysVisible: number): IsoDateWindow[] {
  const start = new Date(now);
  
  // Always use daysVisible - no end-of-week capping
  // This ensures "what you see is what you get" behavior
  // Add (daysVisible - 1) days, then set to end of that day to get exactly daysVisible calendar days
  const effectiveDays = Math.max(daysVisible, 1);
  const end = setEndOfDay(
    new Date(now.getTime() + (effectiveDays - 1) * 24 * 60 * 60 * 1000)
  );
  
  return [
    {
      start: start.toISOString(),
      end: end.toISOString(),
    },
  ];
}
